/**
 * What the page must be told about the deployment it belongs to.
 * Stories VC-019 and VC-020.
 *
 * Stories VC-019, VC-020 and VC-021 — one each for three constants, which
 * is how many refusals it took for the pattern to be visible.
 *
 * Every value here is a fact about a deployment that a page cannot
 * discover, and before these stories it guessed each one: the API's origin
 * from `location`, the tenant and the agent from constants. All three
 * guesses were right while one process served one tenant and one catalogue
 * on one origin, which is every development setup and no deployment.
 *
 * Each default is what that development setup produces with nothing
 * configured on either side, so the two halves agree by construction rather
 * than by a developer matching them up.
 *
 * # Where the runtime is
 *
 * Its own module because the decision has three rules and the component it
 * came from has none that can be tested: `readSettings` read `location`
 * directly, so the only way to exercise it was to load a page.
 *
 * The rules, in the order they are applied:
 *
 * 1. `?endpoint=` wins. The two-port development setup the README describes
 *    serves the page and the runtime from different ports, and typing one
 *    parameter is how a developer says so.
 * 2. Otherwise `VITE_API_ORIGIN`, compiled into the bundle
 *    (`deployment_lld.md` §8.5). A deployment puts the page on one name and
 *    the runtime on another (§4.4), and nothing in a page can discover the
 *    second — a page that guessed would guess its own.
 * 3. Otherwise the page's own origin, which is right whenever one server
 *    serves both.
 *
 * Rule 3 was the whole of it until DEP-007 gave the page its own origin, at
 * which point the deployed page began posting to itself: `location.host` is
 * a CDN, a CDN answers with the page, and a `POST /v1/sessions` came back as
 * HTML.
 */

/** The path the voice socket is served on. `voice_server_lld.md` §13. */
const SOCKET_PATH = "/v1/voice";

/**
 * The tenant a deployment serves unless told otherwise. It is the same
 * default `Settings::from_env` uses, so a development setup where neither
 * side is configured agrees by construction.
 */
const DEFAULT_TENANT = "acme";

/**
 * The agent a session starts with unless told otherwise. It is the entry
 * agent of `config/poc.voice.yaml`, which is the default `VOICE_CONFIG`, so
 * a development setup with neither side configured agrees by construction.
 * `endpoints.test.mjs` reads that document and checks it, because the two
 * are otherwise tied by nothing.
 */
const DEFAULT_AGENT = "poc.shipping";

/**
 * @param {string} search - `location.search`, with or without its `?`.
 * @param {string} origin - `location.origin`, the page's own.
 * @param {Record<string, string | undefined>} env - `import.meta.env`.
 * @returns {{ endpoint: string, base: string, tenant: string, agent: string }}
 */
export function resolveEndpoints(search, origin, env) {
  const params = new URLSearchParams(search);

  // Rule 2 and 3. Trailing slashes are stripped because both are
  // concatenated with a path, and an origin written with one is the normal
  // way to write it.
  const api = trimEnd(params.get("api") ?? env?.VITE_API_ORIGIN ?? origin);

  const endpoint = params.get("endpoint") ?? `${toWebSocket(api)}${SOCKET_PATH}`;

  // The runtime's own endpoints (§24.4) are served by whatever serves the
  // socket. Derived from `endpoint` rather than from `api` so that a
  // developer who gave rule 1 gets one answer and not two: `?endpoint=`
  // pointing at one process and `base` at another is the shape of a bug
  // nobody would look for.
  const base = params.get("base") ?? trimEnd(stripSocketPath(toHttp(endpoint)));

  // Not a claim: `agent_runtime_lld.md` §40.4 takes the tenant from the
  // credential and keeps the body's copy only so a disagreement is
  // detectable. Which makes it something the page is told rather than
  // something it decides — and it was deciding, which is a 403 on the
  // first session a signed-in user starts (VC-020).
  const tenant =
    params.get("tenant") ?? env?.VITE_TENANT ?? DEFAULT_TENANT;

  // The caller names the agent — `agent_runtime_lld.md` §40.1, and a
  // catalogue holding several is the point. Which ones exist is the
  // deployment's, because the document in `VOICE_CONFIG` decides, so the
  // page is told rather than guessing: asking for one a deployment does
  // not define is 404 `agent_not_found` (VC-021).
  const agent = params.get("agent") ?? env?.VITE_AGENT ?? DEFAULT_AGENT;

  return { endpoint, base, tenant, agent };
}

/** @param {string} url */
function toWebSocket(url) {
  return url.replace(/^http/, "ws");
}

/** @param {string} url */
function toHttp(url) {
  return url.replace(/^ws/, "http");
}

/** @param {string} url */
function stripSocketPath(url) {
  return url.endsWith(SOCKET_PATH) ? url.slice(0, -SOCKET_PATH.length) : url;
}

/** @param {string} url */
function trimEnd(url) {
  return url.replace(/\/+$/, "");
}
